import { forwardRef, memo, useCallback, useContext, useEffect, type Component, type ReactElement } from 'react';
import head from 'lodash/head';
import last from 'lodash/last';
// Material UI imports
import { type PaginationProps } from '@mui/material/Pagination';
// Skillmore UI Components
import { MAX_PAGE_SIZE, MIN_PAGE_SIZE } from '@empathco/ui-components/src/config/params';
import { ReactComponent } from '@empathco/ui-components/src/helpers/react';
import Pagination, { hasPaginationElements } from '@empathco/ui-components/src/elements/Pagination';
import {
  type GridPaginationProps,
  PAGINATION_CONTROLS_DISPLAY_NAME, hasPagination
} from '@empathco/ui-components/src/components/CardsGrid';
// local imports
import { getSettingsIntValue } from '../helpers/context';
import { FilterValues } from '../hooks/useFilters';
import { DataContext } from '../context';

type PaginationControlsProps = GridPaginationProps & {
  readonly settingsId?: string;
  readonly init?: FilterValues | null;
  readonly loading?: boolean | null;
  readonly totalMessage?: string;
  readonly currentPage: number;
  readonly pageSizes?: readonly number[];
  readonly onPageSelected: (page: number) => void;
  readonly onPageSize: (pageSize: number) => void;
  readonly disabled?: boolean | null;
}

export type PaginationControlsComponent = ReactComponent<PaginationControlsProps>;
export { hasPagination };

const PaginationControls = forwardRef<Component<PaginationProps>, PaginationControlsProps>(({
  settingsId,
  init,
  loaded,
  pending = false,
  loading = false,
  total,
  totalMessage,
  totalOverride,
  currentPage,
  pageSizes,
  onPageSelected,
  onPageSize,
  disabled: parentDisabled = false
}, ref) => {
  const minPageSize = head(pageSizes) || MIN_PAGE_SIZE;
  const maxPageSize = last(pageSizes) || MAX_PAGE_SIZE;

  const {
    settings: { data: settingsData, pending: pendingSettings, failed: failedSettings },
    settingsUpdate: { pending: pendingSettingsUpdate }, updateSettings
  } = useContext(DataContext);
  const settingsLoaded = !settingsId || (pendingSettings === false && failedSettings === false && Boolean(settingsData));
  const settings = settingsLoaded ? settingsData : null;

  const settingsPageSize = (
    init ? init.page_size : settingsId && getSettingsIntValue(settings, `${settingsId}__pagesize`)
  ) || undefined;

  const handlePageSizeChange = useCallback((pSize: number): void => {
    if (settingsId && settingsPageSize !== pSize) updateSettings?.({
      [`${settingsId}__pagesize`]: pSize
    });
    if (onPageSize) onPageSize(pSize);
  }, [onPageSize, settingsId, settingsPageSize, updateSettings]);

  useEffect(() => {
    if (settingsLoaded && !pendingSettingsUpdate) {
      const pSize = init ? init.page_size
        : (settingsId && getSettingsIntValue(settings, `${settingsId}__pagesize`)) || undefined;
      if (onPageSize) onPageSize(pSize && pSize > minPageSize && pSize <= maxPageSize ? pSize : minPageSize);
    }
  }, [settingsLoaded, pendingSettingsUpdate, minPageSize, maxPageSize, settings, settingsId, init, onPageSize]);

  // pagination must be invisible in this case:
  if (!hasPaginationElements(loaded, total, pending)) return (totalOverride as ReactElement) || null;

  return (
    <Pagination
        ref={ref}
        loaded={loaded}
        pending={pending}
        loading={loading}
        total={total}
        totalMessage={totalMessage}
        totalOverride={totalOverride}
        currentPage={currentPage}
        pageSize={settingsLoaded && !pendingSettingsUpdate ? settingsPageSize : undefined}
        pageSizes={pageSizes}
        onPageSelected={onPageSelected}
        onPageSize={handlePageSizeChange}
        disabled={parentDisabled || !settingsLoaded || pendingSettingsUpdate}
    />
  );
});

PaginationControls.displayName = PAGINATION_CONTROLS_DISPLAY_NAME;

const PaginationControlsMemo = memo(PaginationControls);

PaginationControlsMemo.displayName = PAGINATION_CONTROLS_DISPLAY_NAME;

export default PaginationControlsMemo;
